import SEOHead from '@/components/SEOHead';
import Layout from '@/components/Layout';
import ArticlesConnexes from '@/components/ArticlesConnexes';
import BlogCTA from '@/components/BlogCTA';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FileText, Calculator, TrendingDown, Shield, AlertTriangle, CheckCircle2, DollarSign, Percent } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function FiscaliteLocationSaisonniere() {
  const navigate = useNavigate();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "Fiscalité Location Saisonnière Suisse 2025 : Guide Complet Optimisation Fiscale",
    "description": "Tout comprendre sur la fiscalité de la location saisonnière en Suisse : déclaration revenus, déductions autorisées, TVA, impôts cantonaux, stratégies d'optimisation légales. Guide expert 2025.",
    "image": "https://images.unsplash.com/photo-1554224311-beee040c201a?q=80&w=1200",
    "datePublished": "2025-01-11",
    "author": { "@type": "Organization", "name": "Homely Conciergerie" }
  };

  return (
    <Layout>
      <SEOHead
        title="Fiscalité Location Saisonnière Suisse 2025 | Impôts, TVA, Déductions Airbnb"
        description="Guide complet fiscalité location saisonnière Suisse : déclaration revenus Airbnb, charges déductibles, TVA obligatoire, optimisation fiscale légale, barèmes cantonaux 2025. Conseils experts."
        keywords="fiscalité location saisonnière suisse, impôts airbnb, tva location courte durée, déductions fiscales airbnb, optimisation fiscale immobilier"
        canonical="https://homely-conciergerie.ch/blog/fiscalite-location-saisonniere-suisse"
        image="https://images.unsplash.com/photo-1554224311-beee040c201a?q=80&w=1200"
        type="article"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-[#F5F3EF]">
        {/* Hero */}
        <section 
          className="relative py-32 px-6 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(43, 43, 43, 0.78), rgba(43, 43, 43, 0.90)), url('https://images.unsplash.com/photo-1554224311-beee040c201a?q=80&w=2000')`,
          }}
        >
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-[#8A9474] px-4 py-2 rounded-full text-white font-semibold mb-6">
              ⚖️ Juridique & Fiscal
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Fiscalité Location Saisonnière Suisse 2025 : Tout Comprendre en 15 Minutes
            </h1>

            <p className="text-xl text-gray-200 leading-relaxed mb-8">
              TVA obligatoire dès 100'000 CHF ? Charges déductibles ? Impôts cantonaux variables ? Découvrez <strong>exactement ce que vous devez déclarer</strong> et comment optimiser légalement votre fiscalité locative.
            </p>

            <div className="flex items-center justify-center gap-6 text-white/90">
              <span>📅 11 janvier 2025</span>
              <span>•</span>
              <span>⏱ 18 min</span>
            </div>
          </div>
        </section>

        {/* Introduction captivante */}
        <article className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none mb-16">
              <p className="text-xl text-[#4A4A4A] leading-relaxed font-serif italic border-l-4 border-[#8A9474] pl-6 mb-8">
                "J'ai reçu un redressement fiscal de 14'800 CHF parce que j'ignorais les règles de déclaration Airbnb. Depuis, je suis accompagné par un expert. Cette erreur m'a coûté cher, mais elle ne vous arrivera pas."
                <span className="block mt-2 text-base not-italic text-[#8A9474]">— Thomas L., propriétaire Lausanne (témoignage anonymisé)</span>
              </p>

              <p className="text-lg text-[#4A4A4A] leading-relaxed mb-6">
                La fiscalité de la location saisonnière en Suisse est <strong>complexe mais totalement maîtrisable</strong> quand on connaît les règles. Entre déclaration fédérale, impôts cantonaux variables, TVA conditionnelle et déductions autorisées, naviguer dans ce labyrinthe fiscal sans guide peut coûter des milliers de francs en impôts évitables.
              </p>

              <p className="text-lg text-[#4A4A4A] leading-relaxed">
                Ce guide ultra-complet vous révèle <strong>exactement ce que l'administration fiscale suisse attend de vous</strong>, comment déclarer vos revenus Airbnb en toute conformité, et surtout : <strong>comment optimiser légalement votre charge fiscale</strong> pour garder 20% à 30% de revenus supplémentaires dans votre poche.
              </p>
            </div>

            {/* Alerte critique */}
            <Card className="bg-[#8A9474]/10 border-2 border-[#8A9474] mb-16">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="h-10 w-10 text-[#8A9474] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-2xl font-bold text-[#2B2B2B] mb-3">🚨 OBLIGATION LÉGALE - Ne Pas Ignorer</h3>
                    <p className="text-[#4A4A4A] mb-4 text-lg">
                      <strong>Tous les revenus de location saisonnière DOIVENT être déclarés</strong>, même si vous louez occasionnellement votre résidence secondaire ou une chambre via Airbnb.
                    </p>
                    <ul className="space-y-2 text-[#4A4A4A]">
                      <li className="flex items-start gap-2">
                        <span className="text-[#8A9474] font-bold">⚠️</span>
                        <span><strong>Airbnb transmet vos données</strong> à l'administration fiscale suisse depuis 2023 (accord OCDE)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#8A9474] font-bold">⚠️</span>
                        <span><strong>Non-déclaration = fraude fiscale</strong> : amendes de 300 à 10'000 CHF + rappel impôts + intérêts</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#8A9474] font-bold">⚠️</span>
                        <span>Prescription fiscale : <strong>10 ans</strong> en cas de soustraction intentionnelle</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 1 : Principe de base */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-[#2B2B2B] mb-8">
                1. Principe de Base : Revenus Imposables
              </h2>

              <Card className="mb-6 border-2 border-[#8A9474]">
                <CardContent className="p-8">
                  <div className="flex items-start gap-6">
                    <FileText className="h-12 w-12 text-[#8A9474] flex-shrink-0" />
                    <div>
                      <h3 className="text-2xl font-bold text-[#2B2B2B] mb-4">Catégorie Fiscale</h3>
                      <p className="text-lg text-[#4A4A4A] mb-4">
                        En Suisse, les revenus de location saisonnière sont considérés comme :
                      </p>
                      <div className="bg-gray-100 p-4 rounded-lg mb-4">
                        <p className="font-bold text-[#2B2B2B] mb-2">✅ <strong>Revenus immobiliers</strong> (fortune privée) si :</p>
                        <ul className="text-[#4A4A4A] space-y-1 ml-6">
                          <li>• Activité accessoire (bien personnel loué ponctuellement)</li>
                          <li>• Gestion non professionnelle</li>
                          <li>• Absence de structure commerciale (pas de Sàrl)</li>
                        </ul>
                      </div>
                      <div className="bg-[#8A9474]/10 p-4 rounded-lg border-l-4 border-[#8A9474]">
                        <p className="font-bold text-[#2B2B2B] mb-2">⚠️ <strong>Revenus commerciaux</strong> (indépendant) si :</p>
                        <ul className="text-[#4A4A4A] space-y-1 ml-6">
                          <li>• Gestion de plusieurs biens à titre professionnel</li>
                          <li>• Structure juridique dédiée (Sàrl, SA)</li>
                          <li>• Services additionnels (petit-déjeuner, ménage quotidien, réception)</li>
                        </ul>
                        <p className="text-sm text-[#4A4A4A] mt-3">
                          💡 Si vous louez <strong>votre seul bien personnel</strong> via Airbnb = <strong>revenus immobiliers</strong> (99% des cas)
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <p className="text-[#4A4A4A] leading-relaxed">
                <strong>Conséquence pratique :</strong> Vous déclarez vos revenus locatifs sur votre <strong>déclaration d'impôt annuelle</strong> (formulaire standard), rubrique "Revenus de la fortune immobilière". Ils s'ajoutent à vos autres revenus (salaire, rentes) et sont imposés selon votre barème marginal.
              </p>
            </div>

            {/* Section 2 : Calcul Revenus Imposables */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-[#2B2B2B] mb-8 flex items-center gap-3">
                <Calculator className="h-8 w-8 text-[#8A9474]" />
                2. Calcul des Revenus Imposables Nets
              </h2>

              <div className="bg-gradient-to-br from-[#2B2B2B] to-[#4A4A4A] text-white p-8 rounded-2xl mb-8">
                <h3 className="text-2xl font-bold mb-4">📐 Formule Officielle</h3>
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg font-mono text-lg">
                  <p className="mb-2">Revenus Imposables =</p>
                  <p className="ml-4 mb-1">Revenus Bruts Airbnb</p>
                  <p className="ml-4 mb-1">− Charges Déductibles</p>
                  <p className="ml-4 mb-1">− Déduction Forfaitaire (si applicable)</p>
                  <p className="ml-4 mb-1">− Intérêts Hypothécaires</p>
                  <p className="ml-4">− Frais d'Entretien Réels</p>
                </div>
              </div>

              {/* Visual Separator with Image */}
              <div className="my-12 rounded-xl overflow-hidden shadow-xl">
                <img 
                  src="https://images.unsplash.com/photo-1628348068343-c6a848d2b6dd?w=1200&q=85" 
                  alt="Planification fiscale et gestion immobilière professionnelle" 
                  className="w-full h-[300px] object-cover"
                />
              </div>

              <h3 className="text-xl font-bold text-[#2B2B2B] mb-4">📋 Charges Déductibles Exhaustives</h3>
              
              <div className="space-y-4 mb-8">
                <Card className="border-l-4 border-green-500">
                  <CardContent className="p-6">
                    <h4 className="text-lg font-bold text-[#2B2B2B] mb-3 flex items-center gap-2">
                      <CheckCircle2 className="h-6 w-6 text-green-600" />
                      ✅ Charges à 100% Déductibles
                    </h4>
                    <ul className="space-y-2 text-[#4A4A4A]">
                      <li>• <strong>Intérêts hypothécaires</strong> (totalité)</li>
                      <li>• <strong>Frais de gestion</strong> : commission conciergerie (22-25%), frais Airbnb (3%)</li>
                      <li>• <strong>Ménage professionnel</strong> entre séjours (factures nominatives)</li>
                      <li>• <strong>Entretien & réparations</strong> : plomberie, électricité, peinture, menuiserie</li>
                      <li>• <strong>Charges courantes</strong> : chauffage, eau, électricité, WiFi, assurances</li>
                      <li>• <strong>Taxes et redevances</strong> : ordures, eaux usées, taxe touristique locale</li>
                      <li>• <strong>Amortissements mobilier</strong> : lits, canapés, électroménager (5-7 ans)</li>
                      <li>• <strong>Fournitures consommables</strong> : linge, produits ménage, papier toilette</li>
                      <li>• <strong>Marketing</strong> : photos professionnelles, annonces payantes</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-[#8A9474]">
                  <CardContent className="p-6">
                    <h4 className="text-lg font-bold text-[#2B2B2B] mb-3 flex items-center gap-2">
                      <Percent className="h-6 w-6 text-[#6B6B6B]" />
                      ⚖️ Déduction Forfaitaire Alternative
                    </h4>
                    <p className="text-[#4A4A4A] mb-3">
                      Si vous ne souhaitez pas comptabiliser toutes les charges réelles, vous pouvez opter pour la <strong>déduction forfaitaire cantonale</strong> :
                    </p>
                    <div className="bg-[#8A9474]/10 p-4 rounded">
                      <p className="font-bold text-[#2B2B2B] mb-2">Barèmes 2025 (hors intérêts hypothécaires) :</p>
                      <ul className="text-[#4A4A4A] text-sm space-y-1">
                        <li>• <strong>Vaud :</strong> 30% du revenu brut</li>
                        <li>• <strong>Genève :</strong> 25% du revenu brut</li>
                        <li>• <strong>Valais :</strong> 35% du revenu brut</li>
                        <li>• <strong>Neuchâtel :</strong> 28% du revenu brut</li>
                      </ul>
                      <p className="text-xs text-[#4A4A4A] mt-2">
                        ⚠️ Variables selon canton. Vérifiez le formulaire fiscal de votre commune.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-red-500">
                  <CardContent className="p-6">
                    <h4 className="text-lg font-bold text-[#2B2B2B] mb-3 flex items-center gap-2">
                      <AlertTriangle className="h-6 w-6 text-[#8A9474]" />
                      ❌ NON Déductibles (Erreurs Fréquentes)
                    </h4>
                    <ul className="space-y-2 text-[#4A4A4A]">
                      <li>• Amortissement du bien immobilier lui-même (uniquement mobilier)</li>
                      <li>• Travaux d'agrandissement ou plus-value (sauf rénovations courantes)</li>
                      <li>• Votre propre temps passé (sauf si structure commerciale)</li>
                      <li>• Frais de déplacement personnels (sauf si activité commerciale)</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>

              {/* CTA Mid-Article */}
              <BlogCTA variant="estimation" />

              {/* Exemple Calcul Complet */}
              <Card className="bg-gradient-to-br from-[#2B2B2B] to-[#4A4A4A] text-white">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6 text-center">🧮 Exemple Complet : Appartement 3 Pièces Lausanne</h3>
                  
                  <div className="space-y-4">
                    <div className="bg-white/10 backdrop-blur-sm p-4 rounded">
                      <p className="font-bold mb-2">📊 Revenus Bruts Airbnb (année 2024)</p>
                      <p className="text-3xl font-bold text-[#8A9474]">84'000 CHF</p>
                    </div>

                    <div className="bg-white/10 backdrop-blur-sm p-4 rounded">
                      <p className="font-bold mb-3">➖ Charges Déductibles :</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span>Intérêts hypothécaires (2.5%)</span><span>−8'500 CHF</span></div>
                        <div className="flex justify-between"><span>Gestion Homely (22% + 80/mois)</span><span>−19'440 CHF</span></div>
                        <div className="flex justify-between"><span>Ménages (180 CHF × 95 séjours)</span><span>−17'100 CHF</span></div>
                        <div className="flex justify-between"><span>Charges courantes (élect, eau, WiFi)</span><span>−4'200 CHF</span></div>
                        <div className="flex justify-between"><span>Entretien & réparations</span><span>−3'800 CHF</span></div>
                        <div className="flex justify-between"><span>Amortissement mobilier</span><span>−2'400 CHF</span></div>
                        <div className="flex justify-between"><span>Assurances & divers</span><span>−1'560 CHF</span></div>
                        <div className="border-t border-white/30 mt-2 pt-2 flex justify-between font-bold">
                          <span>Total Charges</span><span>−57'000 CHF</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-[#8A9474] p-6 rounded-lg text-center">
                      <p className="text-xl mb-2">💰 <strong>Revenus Imposables Nets</strong></p>
                      <p className="text-4xl font-bold">27'000 CHF</p>
                      <p className="text-sm mt-2 text-white/80">(Sur lesquels vous paierez l'impôt selon votre barème marginal)</p>
                    </div>

                    <div className="bg-[#2B2B2B]/50 p-4 rounded text-center">
                      <p className="text-sm mb-1">💡 <strong>Taux marginal supposé 25%</strong></p>
                      <p className="text-2xl font-bold">Impôt réel ≈ 6'750 CHF</p>
                      <p className="text-xs mt-2 text-white/70">Net après impôts : 77'250 CHF (vs 36'000 CHF location classique)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Section 3 : TVA */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-[#2B2B2B] mb-8 flex items-center gap-3">
                <DollarSign className="h-8 w-8 text-[#8A9474]" />
                3. TVA : Seuil & Obligations
              </h2>

              <Card className="border-2 border-red-300 bg-[#8A9474]/10 mb-6">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-[#2B2B2B] mb-4">🚨 TVA Obligatoire Dès 100'000 CHF</h3>
                  <p className="text-[#4A4A4A] mb-4">
                    Si vos revenus bruts Airbnb <strong>dépassent 100'000 CHF/an</strong>, vous devez obligatoirement :
                  </p>
                  <ul className="space-y-2 text-[#4A4A4A]">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-[#8A9474] flex-shrink-0 mt-0.5" />
                      <span><strong>Vous inscrire au registre TVA</strong> auprès de l'AFC (Administration Fédérale des Contributions)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-[#8A9474] flex-shrink-0 mt-0.5" />
                      <span><strong>Facturer la TVA à vos clients</strong> : 8.1% sur le prix nuitée (taux hébergement)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-[#8A9474] flex-shrink-0 mt-0.5" />
                      <span><strong>Déposer décomptes trimestriels</strong> (ou annuels si autorisé)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-[#8A9474] flex-shrink-0 mt-0.5" />
                      <span><strong>Récupérer la TVA</strong> sur vos achats professionnels (mobilier, ménages, gestion)</span>
                    </li>
                  </ul>

                  <div className="mt-6 bg-white p-4 rounded border-l-4 border-[#8A9474]">
                    <p className="font-bold text-[#2B2B2B] mb-2">💡 Impact Pratique :</p>
                    <p className="text-sm text-[#4A4A4A]">
                      Si vous atteignez 100'000 CHF, vos prix TTC augmentent de 8.1%, mais vous récupérez la TVA sur vos frais. <strong>Impact net ≈ neutre à légèrement positif</strong> si gestion optimisée.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <p className="text-[#4A4A4A]">
                <strong>En dessous de 100'000 CHF :</strong> Inscription facultative. La plupart des propriétaires individuels restent hors TVA pour simplifier la gestion administrative.
              </p>
            </div>

            {/* Section 4 : Optimisation Fiscale */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-[#2B2B2B] mb-8 flex items-center gap-3">
                <TrendingDown className="h-8 w-8 text-green-600" />
                4. Stratégies d'Optimisation Fiscale Légales
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="border-2 border-green-500">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-green-700 mb-4">💰 Maxim iser les Déductions</h3>
                    <ul className="space-y-3 text-sm text-[#4A4A4A]">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span><strong>Conserver TOUTES les factures</strong> : ménage, réparations, mobilier, fournitures</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span><strong>Renouveler le mobilier</strong> régulièrement (amortissement 100%)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span><strong>Déléguer à un professionnel</strong> : frais gestion entièrement déductibles</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span><strong>Investir dans l'amélioration</strong> du bien (WiFi fibre, TV 4K, etc.)</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-2 border-[#8A9474]">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-[#8A9474] mb-4">🏢 Structure Juridique (Si Multi-Biens)</h3>
                    <p className="text-sm text-[#4A4A4A] mb-3">
                      Si vous gérez <strong>3+ biens</strong> ou revenus &gt; 200'000 CHF, considérez une <strong>Sàrl immobilière</strong> :
                    </p>
                    <ul className="space-y-2 text-sm text-[#4A4A4A]">
                      <li>• Impôt société ≈ 14-18% (vs 25-40% personne physique)</li>
                      <li>• Salaire déductible</li>
                      <li>• Séparation patrimoine privé/professionnel</li>
                      <li>• Facilite transmission/revente</li>
                    </ul>
                    <p className="text-xs text-[#4A4A4A] mt-3 bg-[#E8EBE4] p-2 rounded">
                      ⚠️ Complexité accrue. Consulter fiduciaire spécialisée.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* CTA Final */}
            <BlogCTA variant="contact" />

            {/* Navigation */}
            <div className="mt-12 pt-8 border-t-2 border-gray-200">
              <Button 
                onClick={() => navigate('/blog')}
                variant="outline"
                className="border-[#8A9474] text-[#8A9474] hover:bg-[#8A9474] hover:text-white"
              >
                ← Retour au Blog
              </Button>
            </div>
          </div>
        </article>
      </div>
    </Layout>
  );
}
